import React, { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
} from "@mui/material"
import CategoryIcon from "@mui/icons-material/Category"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import {
  fetchProductCategories,
  createProductCategory,
  updateProductCategory,
  deleteProductCategory,
  fetchCategoryOptions,
  createCategoryOption,
  updateCategoryOption,
  deleteCategoryOption,
} from "helpers/productCategoryApi"

const CategoriesTab = ({ onSnackbar }) => {
  const { t } = useTranslation()

  // Data state
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [optionsLoading, setOptionsLoading] = useState(false)

  // Category dialog
  const [catDialogOpen, setCatDialogOpen] = useState(false)
  const [editingCat, setEditingCat] = useState(null)
  const [catForm, setCatForm] = useState({ name: "", description: "" })
  const [catSaving, setCatSaving] = useState(false)

  // Option dialog
  const [optDialogOpen, setOptDialogOpen] = useState(false)
  const [editingOpt, setEditingOpt] = useState(null)
  const [optForm, setOptForm] = useState({ option_name: "", option_value: "", description: "", atr1: "", atr2: "" })
  const [optSaving, setOptSaving] = useState(false)

  // Load categories on mount
  useEffect(() => {
    loadCategories()
  }, [])

  // Load options when category is selected
  useEffect(() => {
    if (selectedCategory) {
      loadOptions(selectedCategory.id)
    } else {
      setOptions([])
    }
  }, [selectedCategory])

  const loadCategories = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchProductCategories()
      setCategories(data)
    } catch (err) {
      console.error("Error loading categories:", err)
      onSnackbar?.({ open: true, message: t("Error loading categories"), severity: "error" })
    } finally {
      setLoading(false)
    }
  }, [onSnackbar, t])

  const loadOptions = useCallback(async (categoryId) => {
    setOptionsLoading(true)
    try {
      const data = await fetchCategoryOptions(categoryId)
      setOptions(data)
    } catch (err) {
      console.error("Error loading options:", err)
      onSnackbar?.({ open: true, message: t("Error loading options"), severity: "error" })
    } finally {
      setOptionsLoading(false)
    }
  }, [onSnackbar, t])

  // --- Category handlers ---
  const handleOpenCatDialog = (cat = null) => {
    if (cat) {
      setEditingCat(cat)
      setCatForm({ name: cat.name || "", description: cat.description || "" })
    } else {
      setEditingCat(null)
      setCatForm({ name: "", description: "" })
    }
    setCatDialogOpen(true)
  }

  const handleCloseCatDialog = () => {
    setCatDialogOpen(false)
    setEditingCat(null)
    setCatForm({ name: "", description: "" })
  }

  const handleSaveCat = async () => {
    if (!catForm.name.trim()) {
      onSnackbar?.({ open: true, message: t("Category name is required"), severity: "error" })
      return
    }
    setCatSaving(true)
    try {
      if (editingCat) {
        await updateProductCategory(editingCat.id, {
          name: catForm.name.trim(),
          description: catForm.description.trim(),
        })
        onSnackbar?.({ open: true, message: t("Category updated successfully"), severity: "success" })
        // Update selected category if it was the one edited
        if (selectedCategory?.id === editingCat.id) {
          setSelectedCategory(prev => ({ ...prev, name: catForm.name.trim(), description: catForm.description.trim() }))
        }
      } else {
        await createProductCategory({
          name: catForm.name.trim(),
          description: catForm.description.trim(),
          is_active: true,
        })
        onSnackbar?.({ open: true, message: t("Category created successfully"), severity: "success" })
      }
      handleCloseCatDialog()
      loadCategories()
    } catch (err) {
      console.error("Error saving category:", err)
      onSnackbar?.({ open: true, message: t("Error saving category"), severity: "error" })
    } finally {
      setCatSaving(false)
    }
  }

  const handleDeleteCat = async (cat) => {
    if (!window.confirm(t("Are you sure you want to delete this category and all its options?"))) return
    try {
      await deleteProductCategory(cat.id)
      onSnackbar?.({ open: true, message: t("Category deleted successfully"), severity: "success" })
      if (selectedCategory?.id === cat.id) {
        setSelectedCategory(null)
        setOptions([])
      }
      loadCategories()
    } catch (err) {
      console.error("Error deleting category:", err)
      onSnackbar?.({ open: true, message: t("Error deleting category"), severity: "error" })
    }
  }

  // --- Option handlers ---
  const handleOpenOptDialog = (opt = null) => {
    if (opt) {
      setEditingOpt(opt)
      setOptForm({
        option_name: opt.option_name || "",
        option_value: opt.option_value || "",
        description: opt.description || "",
        atr1: opt.atr1 || "",
        atr2: opt.atr2 || "",
      })
    } else {
      setEditingOpt(null)
      setOptForm({ option_name: "", option_value: "", description: "", atr1: "", atr2: "" })
    }
    setOptDialogOpen(true)
  }

  const handleCloseOptDialog = () => {
    setOptDialogOpen(false)
    setEditingOpt(null)
    setOptForm({ option_name: "", option_value: "", description: "", atr1: "", atr2: "" })
  }

  const handleSaveOpt = async () => {
    if (!optForm.option_name.trim()) {
      onSnackbar?.({ open: true, message: t("Option name is required"), severity: "error" })
      return
    }
    setOptSaving(true)
    try {
      if (editingOpt) {
        await updateCategoryOption(editingOpt.id, {
          option_name: optForm.option_name.trim(),
          option_value: optForm.option_value.trim(),
          description: optForm.description.trim(),
          atr1: optForm.atr1.trim(),
          atr2: optForm.atr2.trim(),
        })
        onSnackbar?.({ open: true, message: t("Option updated successfully"), severity: "success" })
      } else {
        await createCategoryOption({
          category_id: selectedCategory.id,
          option_name: optForm.option_name.trim(),
          option_value: optForm.option_value.trim(),
          description: optForm.description.trim(),
          atr1: optForm.atr1.trim(),
          atr2: optForm.atr2.trim(),
          is_active: true,
        })
        onSnackbar?.({ open: true, message: t("Option created successfully"), severity: "success" })
      }
      handleCloseOptDialog()
      loadOptions(selectedCategory.id)
    } catch (err) {
      console.error("Error saving option:", err)
      onSnackbar?.({ open: true, message: t("Error saving option"), severity: "error" })
    } finally {
      setOptSaving(false)
    }
  }

  const handleDeleteOpt = async (opt) => {
    if (!window.confirm(t("Are you sure you want to delete this option?"))) return
    try {
      await deleteCategoryOption(opt.id)
      onSnackbar?.({ open: true, message: t("Option deleted successfully"), severity: "success" })
      loadOptions(selectedCategory.id)
    } catch (err) {
      console.error("Error deleting option:", err)
      onSnackbar?.({ open: true, message: t("Error deleting option"), severity: "error" })
    }
  }

  return (
    <>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 40, height: 40, bgcolor: "#fef3c7", color: "#d97706" }}>
            <CategoryIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {t("Product Categories")}
            </Typography>
            <Typography variant="body2" color="#6b7280">
              {t("Manage product categories and their options")}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenCatDialog()}
          sx={{
            bgcolor: "#3b82f6",
            "&:hover": { bgcolor: "#2563eb" },
            borderRadius: 2,
            textTransform: "none",
          }}
        >
          {t("Add Category")}
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ minHeight: 300 }}>
          {/* Left panel: Categories list */}
          <Box sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }}>
            <Typography variant="caption" fontWeight={600} color="#475569" sx={{ mb: 1, display: "block" }}>
              {t("Categories")} ({categories.length})
            </Typography>
            <Stack spacing={0.75}>
              {categories.length === 0 ? (
                <Typography variant="body2" color="#9ca3af" sx={{ py: 3, textAlign: "center" }}>
                  {t("No categories yet")}
                </Typography>
              ) : (
                categories.map(cat => (
                  <Card
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat)}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      border: selectedCategory?.id === cat.id
                        ? "2px solid #3b82f6"
                        : "1px solid #e5e7eb",
                      bgcolor: selectedCategory?.id === cat.id ? "#eff6ff" : "#fff",
                      borderRadius: 2,
                      transition: "all 0.15s ease",
                      "&:hover": {
                        borderColor: "#93c5fd",
                        bgcolor: selectedCategory?.id === cat.id ? "#eff6ff" : "#f8fafc",
                      },
                    }}
                    elevation={0}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600} color="#1e293b" noWrap>
                          {cat.name}
                        </Typography>
                        {cat.description && (
                          <Typography variant="caption" color="#64748b" noWrap>
                            {cat.description}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0}>
                        <IconButton
                          size="small"
                          onClick={e => { e.stopPropagation(); handleOpenCatDialog(cat) }}
                          sx={{ color: "#64748b" }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={e => { e.stopPropagation(); handleDeleteCat(cat) }}
                          sx={{ color: "#ef4444" }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Card>
                ))
              )}
            </Stack>
          </Box>

          {/* Right panel: Options table */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {selectedCategory ? (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight={600} color="#475569">
                    {t("Options for")} <Chip label={selectedCategory.name} size="small" sx={{ fontWeight: 600, ml: 0.5 }} />
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenOptDialog()}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      borderColor: "#3b82f6",
                      color: "#3b82f6",
                      "&:hover": { borderColor: "#2563eb", bgcolor: "#eff6ff" },
                    }}
                  >
                    {t("Add Option")}
                  </Button>
                </Stack>

                {optionsLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Option Name")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Value")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Description")}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Atr1</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Atr2</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: "#475569" }}>{t("Actions")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {options.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#6b7280" }}>
                              {t("No options yet. Click 'Add Option' to create one.")}
                            </TableCell>
                          </TableRow>
                        ) : (
                          options.map(opt => (
                            <TableRow key={opt.id} hover>
                              <TableCell>
                                <Typography fontWeight={500}>{opt.option_name}</Typography>
                              </TableCell>
                              <TableCell>
                                {opt.option_value ? (
                                  <Chip label={opt.option_value} size="small" sx={{ bgcolor: "#e0e7ff", color: "#4338ca" }} />
                                ) : "-"}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="#64748b">
                                  {opt.description || "-"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="#64748b">
                                  {opt.atr1 || "-"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="#64748b">
                                  {opt.atr2 || "-"}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <IconButton size="small" onClick={() => handleOpenOptDialog(opt)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleDeleteOpt(opt)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : (
              <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                minHeight: 200,
                border: "2px dashed #e2e8f0",
                borderRadius: 2,
                bgcolor: "#f8fafc",
              }}>
                <Typography variant="body2" color="#94a3b8">
                  {t("Select a category to manage its options")}
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      )}

      {/* Category Dialog */}
      <Dialog open={catDialogOpen} onClose={handleCloseCatDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
          {editingCat ? t("Edit Category") : t("Add Category")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={t("Category Name")}
              value={catForm.name}
              onChange={e => setCatForm(prev => ({ ...prev, name: e.target.value }))}
              required
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              size="small"
              label={t("Description")}
              value={catForm.description}
              onChange={e => setCatForm(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={handleCloseCatDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSaveCat}
            disabled={catSaving}
            sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
          >
            {catSaving ? <CircularProgress size={20} color="inherit" /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Option Dialog */}
      <Dialog open={optDialogOpen} onClose={handleCloseOptDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
          {editingOpt ? t("Edit Option") : t("Add Option")}
          {selectedCategory && (
            <Typography variant="body2" color="#64748b">
              {selectedCategory.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={t("Option Name")}
              value={optForm.option_name}
              onChange={e => setOptForm(prev => ({ ...prev, option_name: e.target.value }))}
              required
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              size="small"
              label={t("Option Value")}
              value={optForm.option_value}
              onChange={e => setOptForm(prev => ({ ...prev, option_value: e.target.value }))}
              helperText={t("Internal value (e.g., W, Y, etc.)")}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              size="small"
              label={t("Description")}
              value={optForm.description}
              onChange={e => setOptForm(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Atr1"
                value={optForm.atr1}
                onChange={e => setOptForm(prev => ({ ...prev, atr1: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                size="small"
                label="Atr2"
                value={optForm.atr2}
                onChange={e => setOptForm(prev => ({ ...prev, atr2: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={handleCloseOptDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSaveOpt}
            disabled={optSaving}
            sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
          >
            {optSaving ? <CircularProgress size={20} color="inherit" /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default CategoriesTab
